import { motion } from 'framer-motion';
import { MessageCircle, AlertTriangle, CheckCircle } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface FrameworkObjectionGuideProps {
  framework: any;
  objections: {
    primary: string;
    secondary: string[];
  };
  className?: string;
  scenarioCode?: string | null;
}

export function FrameworkObjectionGuide({
  framework,
  objections,
  className,
  scenarioCode,
}: FrameworkObjectionGuideProps) {
  const { t } = useTranslation();
  const [selectedStep, setSelectedStep] = useState<string | null>(null);
  const [selectedObjection, setSelectedObjection] = useState<string | null>(
    null,
  );

  if (!framework || !objections) return null;

  // Check if this is a Prudential Malaysia demo
  const isPrudentialMalaysia =
    scenarioCode === 'prudential-malaysia-1' ||
    scenarioCode === 'prudential-malaysia-2' ||
    scenarioCode === 'prudential-malaysia-3' ||
    scenarioCode === 'prudential-malaysia-4';
  const isPrudentialMalaysiaReverse =
    scenarioCode === 'prudential-malaysia-3' ||
    scenarioCode === 'prudential-malaysia-4';
  const isPrudentialMalaysiaPrusignature =
    scenarioCode === 'prudential-prusignature-plus-en' ||
    scenarioCode === 'prudential-prusignature-plus-ms';
  const isPrusignaturePlusReverse =
    scenarioCode === 'prudential-prusignature-plus-reverse-en' ||
    scenarioCode === 'prudential-prusignature-plus-reverse-ms';

  return (
    <div className={className}>
      <h2 className="text-lg font-semibold mb-4 flex items-center">
        <MessageCircle className="w-5 h-5 mr-2" />
        {t('custom.roleplay.objectionHandling.guide')}
      </h2>

      {/* Primary Objection */}
      <div className="mb-4">
        <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">
          {t('custom.roleplay.objectionHandling.primaryObjection')}
        </h3>
        <div
          className={`bg-red-50 p-3 rounded-lg transition-colors ${!(isPrudentialMalaysiaReverse || isPrusignaturePlusReverse) ? 'cursor-pointer hover:bg-red-100' : ''}`}
          onClick={() => {
            if (!isPrudentialMalaysiaReverse && !isPrusignaturePlusReverse) {
              setSelectedObjection(
                selectedObjection === 'primary' ? null : 'primary',
              );
            }
          }}
        >
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-gray-700">{objections?.primary}</p>
          </div>
        </div>

        {selectedObjection === 'primary' && !isPrusignaturePlusReverse && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="mt-2 bg-white border border-gray-200 rounded-lg p-3"
          >
            <h4 className="text-sm font-medium mb-2">
              {t('custom.roleplay.objectionHandling.suggested3FsResponse')}:
            </h4>
            <div className="space-y-3">
              <div className="flex items-start">
                <div className="bg-blue-100 text-blue-800 font-medium px-2 py-1 rounded text-xs mr-2 mt-0.5">
                  {t('custom.roleplay.objectionHandling.feel')}
                </div>
                <p className="text-sm">
                  {isPrudentialMalaysia
                    ? `"${t('custom.roleplay.objectionHandling.feelExampleOneSentence')}"`
                    : isPrudentialMalaysiaPrusignature
                      ? `"${framework.steps.find((step: any) => step.name === 'Feel')?.examples[0]}"`
                      : `"${t('custom.roleplay.objectionHandling.feelExample')} ${objections.primary.toLowerCase()}. ${t('custom.roleplay.objectionHandling.commonConcern')}."`}
                </p>
              </div>
              <div className="flex items-start">
                <div className="bg-purple-100 text-purple-800 font-medium px-2 py-1 rounded text-xs mr-2 mt-0.5">
                  {t('custom.roleplay.objectionHandling.felt')}
                </div>
                <p className="text-sm">
                  {isPrudentialMalaysiaPrusignature ? (
                    `"${framework.steps.find((step: any) => step.name === 'Felt')?.examples[0]}"`
                  ) : (
                    <>"{t('custom.roleplay.objectionHandling.feltExample')}"</>
                  )}
                </p>
              </div>
              <div className="flex items-start">
                <div className="bg-green-100 text-green-800 font-medium px-2 py-1 rounded text-xs mr-2 mt-0.5">
                  {t('custom.roleplay.objectionHandling.found')}
                </div>
                <p className="text-sm">
                  {isPrudentialMalaysiaPrusignature ? (
                    `"${framework.steps.find((step: any) => step.name === 'Found')?.examples[0]}"`
                  ) : (
                    <>"{t('custom.roleplay.objectionHandling.foundExample')}"</>
                  )}
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Secondary Objections */}
      <div className="mb-6">
        <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">
          {t('custom.roleplay.objectionHandling.secondaryObjections')}
        </h3>
        <div className="space-y-2">
          {objections?.secondary.map((objection, index) => (
            <div
              key={index}
              className="flex items-start gap-2 bg-gray-50 p-3 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
              onClick={() => {
                if (
                  !isPrudentialMalaysiaReverse &&
                  !isPrusignaturePlusReverse
                ) {
                  setSelectedObjection(
                    selectedObjection === `secondary-${index}`
                      ? null
                      : `secondary-${index}`,
                  );
                }
              }}
            >
              <AlertTriangle className="w-5 h-5 text-yellow-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-gray-600">{objection}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Framework Steps Reference */}
      {framework.steps?.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">
            {t('custom.roleplay.objectionHandling.frameworkTitleTemplate', {
              name: framework.name,
            })}
          </h3>
          <div className="space-y-2">
            {framework.steps.map((step: any) => (
              <motion.div
                key={step.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white border border-gray-200 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => {
                    if (!isPrusignaturePlusReverse) {
                      setSelectedStep(
                        selectedStep === step.name ? null : step.name,
                      );
                    }
                  }}
                  className={`w-full px-4 py-3 flex items-center justify-between transition-colors ${!isPrusignaturePlusReverse ? 'hover:bg-gray-50 cursor-pointer' : 'cursor-default'}`}
                >
                  <div className="flex items-center">
                    <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                    <span className="text-sm font-medium text-left">
                      {step.name}
                    </span>
                  </div>
                  {!isPrusignaturePlusReverse && (
                    <svg
                      className={`w-5 h-5 transform transition-transform ${
                        selectedStep === step.name ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  )}
                </button>

                {selectedStep === step.name && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="p-4 border-t border-gray-200"
                  >
                    <p className="text-sm text-gray-600 mb-3">
                      {step.description}
                    </p>

                    <div className="space-y-2">
                      {step.examples.map((example: string, i: number) => (
                        <div
                          key={i}
                          className="text-sm bg-blue-50 text-blue-700 p-3 rounded-lg flex justify-between items-center"
                        >
                          <span>"{example}"</span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigator.clipboard.writeText(example);
                            }}
                            className="text-xs text-blue-600 hover:text-blue-800 bg-blue-100 px-2 py-1 rounded"
                          >
                            {t('custom.roleplay.buttons.copy')}
                          </button>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
